import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Menu, X, ArrowRight, Sun, Moon } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const links = user
    ? [
        { to: "/dashboard", label: "Dashboard" },
        { to: "/operations", label: "Operations" },
        { to: "/signals", label: "Signals" },
        { to: "/reports", label: "Reports" },
        { to: "/monitoring", label: "Monitoring" },
        { to: "/boardroom", label: "Boardroom" },
        { to: "/autonomy", label: "Autopilot" },
        { to: "/compare", label: "Compare" },
        { to: "/account", label: "Account" },
        ...(user.is_admin ? [{ to: "/admin", label: "Admin" }] : []),
      ]
    : [{ to: "/compare", label: "Compare Me" }, { to: "/feedback", label: "Feedback" }, { to: "/support", label: "Support" }];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl backdrop-saturate-150">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-black shadow-md shadow-klein/30 ring-1 ring-white/10 transition-transform group-hover:scale-105">
            <img src="/aria-mark.png" alt="A.R.I.A." className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight text-brand-dark">A.R.I.A.</span>
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === l.to ? "text-klein" : "text-slate-600 hover:bg-slate-100 hover:text-brand-dark"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <div className="ml-3 flex items-center gap-3">
              <span className="mono-label hidden xl:inline">{user.email}</span>
              <ThemeToggle theme={theme} toggle={toggle} />
              <button data-testid="nav-logout" onClick={handleLogout}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50">
                Sign out
              </button>
            </div>
          ) : (
            <div className="ml-3 flex items-center gap-2">
              <ThemeToggle theme={theme} toggle={toggle} />
              <button data-testid="nav-login" onClick={() => navigate("/login")}
                className="rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100">
                Sign in
              </button>
              <button data-testid="nav-cta" onClick={() => navigate("/analyze")}
                className="group inline-flex items-center gap-1.5 rounded-full bg-brand-dark px-5 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-klein/25">
                Analyze Free <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          )}
        </nav>

        <div className="flex items-center gap-1 lg:hidden">
          <ThemeToggle theme={theme} toggle={toggle} />
          <button data-testid="nav-mobile-toggle" aria-label="Toggle menu" aria-expanded={open} className="flex h-11 w-11 items-center justify-center" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-3 lg:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className={`block border-b border-slate-100 py-3 text-base ${location.pathname === l.to ? "font-semibold text-klein" : "text-slate-700"}`}>
              {l.label}
            </Link>
          ))}
          {user ? (
            <button onClick={handleLogout} className="block w-full py-3 text-left text-base text-danger">Sign out</button>
          ) : (
            <div className="flex flex-col gap-2 pt-3">
              <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-base text-slate-700">Sign in</Link>
              <button data-testid="nav-mobile-cta" className="w-full rounded-full bg-brand-dark py-3 text-base font-semibold text-white" onClick={() => { setOpen(false); navigate("/analyze"); }}>
                Analyze Free
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}


function ThemeToggle({ theme, toggle }) {
  return (
    <button
      data-testid="theme-toggle"
      onClick={toggle}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-klein hover:text-klein dark:border-white/15 dark:text-slate-300"
    >
      {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}
